import {
    type FC
} from "react";
import {
    Pressable,
    Platform,
    View
} from "react-native";
import type INavigationMenuTriggerProps from "./type";
import type {
    NavigationMenuTriggerWebPropsType
} from "./type";
import stylesheet, {
    getNavigationMenuTriggerColors,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    INTERACTION_STATE_DATA_KEY,
    useInteractionState
} from "../../../../helpers/interaction";
import {
    usePointerType
} from "../../../../helpers/pointer";
import {
    useNavigationMenuContext
} from "../../context";
import {
    ChevronDown as ChevronDownIcon,
    ChevronUp as ChevronUpIcon
} from "lucide-react-native";
import {
    getControlMeasures,
    getHitArea
} from "../../../../helpers/theme/control";
import {
    getItemTitleVariant
} from "../../../item/stylesheet";
import {
    getNavigationMenuBarOffset
} from "../../util";
import {
    openLink
} from "../../../../helpers/link";
import AnimatedIcon from "../../../animatedIcon";
import StateLayer from "../../../stateLayer";
import Text from "../../../text";

const NavigationMenuTrigger: FC<INavigationMenuTriggerProps> = ({
    customTheme: customThemeProp,
    hasContent,
    isDisabled,
    isActive,
    itemKey,
    onPress,
    title,
    href
}) => {
    const context = useNavigationMenuContext({
        part: "Item"
    });

    const customTheme = customThemeProp ?? context?.customTheme;

    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const pointerType = usePointerType();

    const size = context?.size ?? "medium";

    const isOpen = hasContent && !!context && context.activeValue === itemKey;

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        onHoverIn: () => {
            if(hasContent) {
                context?.handleScheduleOpen(itemKey);

                return;
            }

            context?.handleScheduleClose();
        },
        onHoverOut: () => {
            context?.handleScheduleClose();
        },
        isDisabled
    });

    const isLayerHovered = !isDisabled && isHovered;

    const isLayerPressed = !isDisabled && (isPressed || isOpen);

    const isEmphasized = isLayerHovered || isLayerPressed || isActive || isFocusVisible;

    const currentMeasures = getControlMeasures({
        controlPaddings,
        controlHeights,
        shapeConfigs,
        iconSizes,
        size
    });

    const hitArea = getHitArea({
        visualSize: currentMeasures.minHeight,
        touchTargets,
        pointerType
    });

    const currentColors = getNavigationMenuTriggerColors({
        isEmphasized,
        isDisabled,
        colors
    });

    const {
        container: containerDynamicStyle,
        indicator: indicatorDynamicStyle,
        content: contentDynamicStyle,
        surface: surfaceDynamicStyle
    } = useStyles({
        barOffset: getNavigationMenuBarOffset({
            controlHeights,
            borders,
            spaces,
            size
        }),
        currentMeasures,
        isFocusVisible,
        currentColors,
        shapeConfigs,
        isDisabled,
        radiuses,
        borders,
        hitArea,
        colors,
        spaces
    });

    if(!context) {
        return null;
    }

    const handleRef = (node: View | null) => {
        context.triggerRefs.current.set(itemKey, node);
    };

    const handlePress = () => {
        if(hasContent) {
            context.handlePress(itemKey);

            return;
        }

        onPress?.();

        context.handleLinkPress();

        openLink({
            href
        });
    };

    const webProps: NavigationMenuTriggerWebPropsType = Platform.OS === "web" ? {
        "aria-current": isActive ? hasContent ? true : "page" : undefined,
        href: !hasContent && !isDisabled ? href : undefined,
        onKeyDown: isDisabled ? undefined : (event) => {
            context.handleTriggerKeyDown({
                itemKey,
                event
            });
        },
        dataSet: isDisabled ? {
            [INTERACTION_STATE_DATA_KEY]: "disabled"
        } : interactionHandlers.dataSet
    } : {};

    const renderChevron = () => {
        if(!hasContent) {
            return null;
        }

        return <AnimatedIcon
            customColor={currentColors.iconColor}
            size={currentMeasures.iconSize}
            fromIcon={ChevronDownIcon}
            customTheme={customTheme}
            isTransitioned={isOpen}
            toIcon={ChevronUpIcon}
            iconTransition="morph"
        />;
    };

    const renderIndicator = () => {
        if(!isActive) {
            return null;
        }

        return <View
            style={[
                stylesheet.indicator,
                indicatorDynamicStyle
            ]}
            aria-hidden={true}
        />;
    };

    return <Pressable
        {...interactionHandlers}
        {...webProps}
        aria-controls={hasContent ? context.getContentId(itemKey) : undefined}
        aria-selected={Platform.OS === "web" ? undefined : isActive}
        aria-expanded={hasContent ? isOpen : undefined}
        role={hasContent ? "button" : "link"}
        aria-disabled={isDisabled}
        style={[
            stylesheet.container,
            containerDynamicStyle
        ]}
        onPress={handlePress}
        disabled={isDisabled}
        ref={handleRef}
    >
        <View
            style={[
                stylesheet.surface,
                surfaceDynamicStyle
            ]}
        >
            <StateLayer
                borderRadius={radiuses.actions}
                isHovered={isLayerHovered}
                isPressed={isLayerPressed}
                borderWidth={borders.line}
                customTheme={customTheme}
                overlayColor="neutral"
            />
            <View
                style={[
                    stylesheet.content,
                    contentDynamicStyle
                ]}
            >
                <Text
                    variant={getItemTitleVariant({
                        titleVariant: currentMeasures.labelVariant,
                        isSelected: isActive,
                        size: "medium"
                    })}
                    customColor={currentColors.titleColor}
                    customTheme={customTheme}
                    numberOfLines={1}
                >
                    {title}
                </Text>
                {renderChevron()}
            </View>
            {renderIndicator()}
        </View>
    </Pressable>;
};
export default NavigationMenuTrigger;
